// src/pages/Customers.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Customers as API, Leads, Quotes, Calls, Visits, Company } from '../services/api';
import { useAuth, ROLES } from '../context/AuthContext';

const EMPTY = { id: 0, company: '', name: '', name1: '', number: '', number1: '',
  email: '', street: '', city: '', gst: '', industry: '', lsource: '', ltype: '', comments: '', location_id: '' };

const STAT_COLOR = { '1':'var(--green)', '2':'var(--yellow)', '3':'var(--faint)',
  '4':'var(--red)', '7':'var(--accent)', '8':'var(--purple)' };
const STAT_LABEL = { '1':'Active', '2':'Follow Up', '3':'Closed',
  '4':'Lost', '7':'Quoted', '8':'Invoiced' };

export default function Customers() {
  const { user }  = useAuth();
  const [list, setList]     = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy]     = useState(true);
  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState('all');
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  // ── History modal state ──────────────────────────────────
  const [historyFor, setHistoryFor] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyErr, setHistoryErr] = useState('');
  const [historyTab, setHistoryTab] = useState('quotes');
  const [historyData, setHistoryData] = useState({
    quotes: [], calls: [], visits: [], statusHistory: [],
  });

  const load = useCallback(() => {
    setBusy(true);
    Promise.all([API.list(), Company.addresses()])
      .then(([custs, addrs]) => { setList(custs); setLocations(addrs); })
      .catch(console.error)
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const locName = id => locations.find(l => String(l.id) === String(id))?.office || null;

  const filtered = list.filter(c => {
    const matchSearch =
      (c.company || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.name    || '').toLowerCase().includes(search.toLowerCase()) ||
      String(c.number || '').includes(search);
    const matchLoc = locFilter === 'all' || String(c.location_id) === String(locFilter);
    return matchSearch && matchLoc;
  });

  const openAdd  = ()  => { setForm({ ...EMPTY, corp_id: user.corp_id, user_id: user.id }); setModal(true); setErr(''); };
  const openEdit = (c) => { setForm({ ...c,     corp_id: user.corp_id, user_id: user.id }); setModal(true); setErr(''); };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.company || !form.name || !form.number) { setErr('Company, Contact Name and Phone are required'); return; }
    setSaving(true); setErr('');
    try { await API.save(form); setModal(false); load(); }
    catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const openHistory = async (c) => {
    setHistoryFor(c);
    setHistoryTab('quotes');
    setHistoryErr('');
    setHistoryBusy(true);
    try {
      const [allQuotes, allCalls, allVisits, statusHistory] = await Promise.all([
        Quotes.list().catch(() => []),
        Calls.list().catch(() => []),
        Visits.list().catch(() => []),
        Leads.history(c.id).catch(() => []),
      ]);
      setHistoryData({
        quotes: allQuotes.filter(q => String(q.customer) === String(c.id)),
        calls: allCalls.filter(x => String(x.customer_id) === String(c.id)),
        visits: allVisits.filter(x => String(x.customer_id) === String(c.id)),
        statusHistory,
      });
    } catch (ex) {
      setHistoryErr(ex.message || 'Failed to load history');
    } finally {
      setHistoryBusy(false);
    }
  };

  const closeHistory = () => { setHistoryFor(null); setHistoryData({ quotes: [], calls: [], visits: [], statusHistory: [] }); };

  const HIST_TABS = [
    ['quotes',        '📋 Quotes',    historyData.quotes.length],
    ['calls',         '📞 Calls',     historyData.calls.length],
    ['visits',        '📍 Visits',    historyData.visits.length],
    ['statusHistory', '🕓 Status',    historyData.statusHistory.length],
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">👥 Customers</div>
        <input className="search-bar" placeholder="Search name, company, phone…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {locations.length > 0 && (
          <select style={{ width: 'auto' }} value={locFilter} onChange={e => setLocFilter(e.target.value)}>
            <option value="all">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.office}</option>)}
          </select>
        )}
        <button className="btn btn-primary" onClick={openAdd}>+ Add</button>
      </div>

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        filtered.length === 0 ? <div className="empty">No customers found</div> :
        filtered.map(c => (
          <div key={c.id} className="list-row">
            <div className="row-main" style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
              <div className="row-title">{c.company}</div>
              <div className="row-sub">
                {c.name}
                {c.number ? ` · 📞 ${c.number}` : ''}
                {c.email  ? ` · ${c.email}` : ''}
                {(c.location_name || locName(c.location_id)) ? ` · 🏢 ${c.location_name || locName(c.location_id)}` : ''}
              </div>
            </div>
            <div className="row-actions">
              <span className="badge" style={{ background: STAT_COLOR[c.lstatus] || 'var(--faint)', color: '#0f172a' }}>
                {STAT_LABEL[c.lstatus] || c.lstatus}
              </span>
              {c.city && <span style={{ color: 'var(--faint)', fontSize: '.78rem' }}>{c.city}</span>}
              <button
                className="btn btn-sm btn-ghost"
                title="View complete history"
                onClick={(e) => { e.stopPropagation(); openHistory(c); }}
              >
                🕓 History
              </button>
            </div>
          </div>
        ))
      }

      {/* ── Add/Edit Modal ── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{form.id ? 'Edit Customer' : 'Add Customer'}</span>
              <button className="btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Company *<input value={form.company}  onChange={set('company')} /></label>
              <label>Industry <input value={form.industry} onChange={set('industry')} /></label>
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Primary Contact *<input value={form.name}  onChange={set('name')} /></label>
              <label>Secondary Contact<input value={form.name1} onChange={set('name1')} /></label>
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Phone *  <input type="tel" value={form.number}  onChange={set('number')} /></label>
              <label>Phone 2  <input type="tel" value={form.number1} onChange={set('number1')} /></label>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label>Email<input type="email" value={form.email} onChange={set('email')} /></label>
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>City  <input value={form.city}   onChange={set('city')} /></label>
              <label>GST No<input value={form.gst}    onChange={set('gst')} /></label>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label>Street Address<input value={form.street} onChange={set('street')} /></label>
            </div>
            {locations.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  Servicing Branch <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(which office owns this account)</span>
                  <select value={form.location_id} onChange={set('location_id')}>
                    <option value="">-- Unassigned --</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.office}</option>)}
                  </select>
                </label>
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label>Comments<textarea rows={2} value={form.comments} onChange={set('comments')} /></label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : form.id ? 'Update' : 'Add Customer'}</button>
              <button className="btn btn-ghost"   onClick={() => setModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal (unchanged) ── */}
      {historyFor && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeHistory()}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <span className="modal-title">🕓 History — {historyFor.company}</span>
              <button className="btn-icon" onClick={closeHistory}>✕</button>
            </div>

            {historyErr && <div className="alert alert-error">{historyErr}</div>}

            {historyBusy ? (
              <div className="spinner-wrap"><div className="spinner" /></div>
            ) : (
              <>
                <div className="tabs" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
                  {HIST_TABS.map(([k, l, c]) => (
                    <button key={k} className={`tab${historyTab === k ? ' active' : ''}`} onClick={() => setHistoryTab(k)}>
                      {l} <span style={{ opacity: .6, fontSize: '.72rem' }}>({c})</span>
                    </button>
                  ))}
                </div>

                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {historyTab === 'quotes' && (
                    historyData.quotes.length === 0 ? <div className="empty">No quotes for this customer</div> :
                    historyData.quotes.map(q => (
                      <div key={q.id} className="list-row" style={{ marginBottom: '.5rem' }}>
                        <div className="row-main">
                          <div className="row-title">{q.name || `Quote #${q.id}`}</div>
                          <div className="row-sub">
                            PO: {q.pono || '—'} · {q.date_entered?.slice(0, 10)}
                            {q.withtax == 1 ? ' · With GST' : ' · Ex-GST'}
                          </div>
                        </div>
                        <div className="row-actions">
                          <span className="badge" style={{
                            background: q.invoiced >= 1 ? 'var(--green)' : 'var(--faint)',
                            color: '#0f172a', fontSize: '.72rem',
                          }}>
                            {q.invoiced >= 1 ? 'Invoiced' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}

                  {historyTab === 'calls' && (
                    historyData.calls.length === 0 ? <div className="empty">No call logs for this customer</div> :
                    historyData.calls.map(c => (
                      <div key={c.id} className="list-row" style={{ marginBottom: '.5rem' }}>
                        <div className="row-main">
                          <div className="row-title">{c.call_type} · {c.phone_number}</div>
                          <div className="row-sub">
                            {c.call_at?.slice(0, 16)}
                            {c.duration_seconds > 0 ? ` · ${c.duration_seconds}s` : ''}
                          </div>
                          {c.notes && <div style={{ color: 'var(--faint)', fontSize: '.78rem', marginTop: 2 }}>{c.notes}</div>}
                        </div>
                        <div className="row-actions">
                          <span className="badge" style={{ background: 'var(--faint)', color: '#0f172a', fontSize: '.72rem' }}>
                            {c.outcome?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}

                  {historyTab === 'visits' && (
                    historyData.visits.length === 0 ? <div className="empty">No visits logged for this customer</div> :
                    historyData.visits.map(v => (
                      <div key={v.id} className="list-row" style={{ marginBottom: '.5rem' }}>
                        <div className="row-main">
                          <div className="row-title">{v.purpose}</div>
                          <div className="row-sub">
                            📅 {v.visit_date}
                            {v.check_in_at ? ` · In: ${v.check_in_at.slice(11, 16)}` : ''}
                          </div>
                          {v.notes && <div style={{ color: 'var(--faint)', fontSize: '.78rem', marginTop: 2 }}>{v.notes}</div>}
                        </div>
                        <div className="row-actions">
                          <span className="badge" style={{ background: 'var(--faint)', color: '#0f172a', fontSize: '.72rem' }}>
                            {v.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}

                  {historyTab === 'statusHistory' && (
                    historyData.statusHistory.length === 0 ? <div className="empty">No status changes recorded</div> :
                    historyData.statusHistory.map((h, i) => (
                      <div key={i} style={{ display: 'flex', gap: '.8rem', marginBottom: '.8rem', padding: '.6rem', background: 'var(--bg)', borderRadius: 8 }}>
                        <span className="badge" style={{ background: STAT_COLOR[h.status_id] || 'var(--faint)', color: '#0f172a', flexShrink: 0 }}>
                          {STAT_LABEL[h.status_id] || h.status_id}
                        </span>
                        <div>
                          <div style={{ fontSize: '.85rem' }}>{h.reason || '—'}</div>
                          <div style={{ color: 'var(--faint)', fontSize: '.75rem' }}>{h.date}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeHistory}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}